import React, { useEffect, useState, useCallback, useMemo } from "react";
import GoogleMapReact from "google-map-react";
import { IGoogleMapProps } from "../../../interfaces/common";
import { Marker, UserMarker } from "./components/Markers/Markers";
import SalonSearchLeftDrawerBar from "../../Guests/SalonSearchMap/SalonSearchLeftDrawerBar";
import useLanguage from "../useLanguage";

const isValidCoordinate = (coord: number): boolean => {
  return !isNaN(coord) && coord >= -90 && coord <= 90;
};

const isValidLng = (lng: number): boolean => {
  return !isNaN(lng) && lng >= -180 && lng <= 180;
};

export const GoogleMap: React.FC<IGoogleMapProps> = ({
  markers,
  guestPosition,
  onMarkerClick,
  center
}) => {  
  const [lang, _setLang] = useLanguage();
  const [mapCenter, setMapCenter] = useState({
    isCenter: false,
    center: { lat: 0, lng: 0},
    zoom: 100,
  });
  const [mapBounds, setMapBounds] = useState<{ ne: { lat: number, lng: number }, sw: { lat: number, lng: number } } | null>(null);
  const [defaultCenter, setDefaultCenter] = useState({
    center: { lat: +32.0631703, lng: +34.768982},
    zoom: 15,
  });

  const updateMapCenter = ( lat?: number, lng?: number) => {
    if(lat && lng && isValidCoordinate(lat) && isValidLng(lng)) {
      setMapCenter((mapCenter) => ({
        ...mapCenter,
        center:{ lat, lng},
        isCenter: true,
        zoom: 100
      }))
    }
  }
  
  const [sideDrawerOpen, setSideDrawerOpen] = React.useState(false);
  const [selectedMarker, setSelectedMarker] = React.useState(null);

  const handleMarkerClick = useCallback((marker:any, idx: number) => {
    onMarkerClick(idx, marker.id);
    if(selectedMarker && marker.id === selectedMarker.id) {
      setSelectedMarker(null)
    } else{
      updateMapCenter(marker.lat, marker.lng);
      setSelectedMarker(marker)
    }
  }, [onMarkerClick, selectedMarker]);

  // Filter markers based on bounds
  const visibleMarkers = useMemo(() => {
    if (!mapBounds || !markers) return markers;
    
    return markers.filter(marker => {
      const { lat, lng } = marker;
      if (!isValidCoordinate(lat) || !isValidLng(lng)) return false;
      
      return (
        lat >= mapBounds.sw.lat &&
        lat <= mapBounds.ne.lat &&
        lng >= mapBounds.sw.lng &&
        lng <= mapBounds.ne.lng
      );
    });
  }, [markers, mapBounds]);

  useEffect(() => {
    if (guestPosition?.lat && guestPosition?.lng) {
      const lat = +guestPosition.lat;
      const lng = +guestPosition.lng;
      if (isValidCoordinate(lat) && isValidLng(lng)) {
        setDefaultCenter((defaultCenter) => ({
          ...defaultCenter,
          center: { lat, lng },
        }));
      }
    }   
  }, [guestPosition]);

  useEffect(()=>{
    if (center?.lat && center?.lng && isValidCoordinate(center.lat) && isValidLng(center.lng)) {
        setMapCenter((mapCenter) => ({
          ...mapCenter,
          center,
          isCenter: true,
          zoom: 100
        }));
    }
  },[center])

  const [draggable, setDraggable] = useState(true);
  const guestLatLng = useMemo(() => {
    if (guestPosition?.lat && guestPosition?.lng) {
      const lat = +guestPosition.lat;
      const lng = +guestPosition.lng;
      if (isValidCoordinate(lat) && isValidLng(lng) && typeof google !== "undefined" && google.maps) {
        return new google.maps.LatLng(lat, lng);
      }
    }
    return null;
  }, [guestPosition]);
  
  return (
    <div style={{ height: "100%", width: "100%" }}>
        <GoogleMapReact
          bootstrapURLKeys={{
            key: process.env.REACT_APP_GOOGLE_MAP_KEY || "AIzaSyCSVSuUVhz6XBgyQ3R0W3NTuzl8p05N8gI",
            language: lang,
            libraries: ["places"],
          }}
          options={{ minZoom: 8, fullscreenControl: false }}
          draggable={draggable}
          onChildMouseDown={(key) => key !== "userMarker" && setDraggable(false)}
          onClick={() => setDraggable(true)}
          defaultCenter={defaultCenter.center}
          defaultZoom={defaultCenter.zoom}
          center={mapCenter.isCenter ? mapCenter.center : defaultCenter.center}
          yesIWantToUseGoogleMapApiInternals={true}
          onChange={({ bounds }) => {
            if (bounds) {
              setMapBounds({
                ne: bounds.ne,
                sw: bounds.sw
              });
            }
          }}
        >
          {visibleMarkers &&
            visibleMarkers.map((marker, idx) => (
              <Marker
                key={`${marker.id}-${marker.lat}-${marker.lng}`}
                isCenter={
                  (center && mapCenter.center.lat === marker.lat && mapCenter.center.lng === marker.lng) ||
                  (selectedMarker && selectedMarker.id === marker.id)
                }
                onMarkerClick={() => handleMarkerClick(marker, idx)}
                lat={marker.lat}
                lng={marker.lng}
                markerInfo={marker.markerInfo}
                setSideDrawerOpen={setSideDrawerOpen}
              />
            ))}
          <UserMarker
            key={"userMarker"}
            lat={defaultCenter.center.lat}
            lng={defaultCenter.center.lng}
          />
        </GoogleMapReact>
        <SalonSearchLeftDrawerBar
          selectedSalonDetails={selectedMarker?.markerInfo}
          guestLatLng={guestLatLng}
          modalIsOpen={sideDrawerOpen}
          setIsOpen={setSideDrawerOpen}
        />
    </div>
  )
};
